import React, { useEffect } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import ArrowForward from '@mui/icons-material/ArrowForward'
import Lottie from 'lottie-react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Column } from 'src/components/Layout/Styled'
import lottieLogin from 'src/modules/Login/WatingForLogin/lottieLogin.json'
import { handleQueueing } from 'src/modules/Login/WatingForLogin/events'
import {
  StyledLoadingProgress,
  StyledProgressBox,
  StyledProgressLoad,
} from 'src/modules/Login/WatingForLogin/Styled'
import {
  checkQueueDone,
  refreshQueuePosition,
} from 'src/redux/slices/queue/events'
import { StyledQueueCard, StyledQueueOverlay } from './Styled'

// หน้าเข้าแถวตอนสมัครอบรม โผล่เมื่อคิวถึงเกณฑ์ REACT_APP_QUEUE_THRESHOLD
const QueueWaiting = () => {
  const dispatch = useDispatch()
  const { ticketId, isWaiting, total, remaining, courseUuid, trainingPlanUuid } =
    useSelector((state) => state.queue, shallowEqual)

  const isMyTurn = remaining <= 0
  // ถึงคิวแล้วต้องเต็มหลอด และเคสเข้าคิวตอนคิวว่าง (total = 0) ก็ถือว่าเต็ม
  // ไม่ปล่อยให้เป็น 0/0 แล้วหลอดว่างทั้งที่ถึงคิวแล้ว
  const loadingPercent = isMyTurn ? 100 : 100 - (remaining / total) * 100

  useEffect(() => {
    if (isWaiting) handleQueueing(loadingPercent)
  }, [isWaiting, loadingPercent])

  // เฝ้าบัตรตั้งแต่มี ticketId ไม่ต้องรอให้ popup โชว์
  // เพราะเคสคิวสั้นแล้วผลไม่กลับมา ต้องมีคนคอยถามสถานะให้ ไม่งั้นหมุนค้างเงียบ
  useEffect(() => {
    if (!ticketId) return

    // ตัวหลัก: ถามลำดับจากเลขบัตร (query เบา)
    const refreshPosition = () => dispatch(refreshQueuePosition())

    // ทางสำรอง: เช็คว่าลงทะเบียนสำเร็จหรือยัง เผื่อบัตรไม่ถูกปิด
    // (เช่น worker คนละเวอร์ชันเป็นคนหยิบไปทำ) - query หนัก ยิงห่าง ๆ พอ
    const refreshRegistered = () => {
      if (courseUuid && trainingPlanUuid) {
        dispatch(checkQueueDone({ uuid: courseUuid, trainingPlanUuid }))
      }
    }

    refreshPosition()
    refreshRegistered()

    // ตอนยังไม่โชว์ popup ถามถี่หน่อย (5 วิ) เพื่อรู้ผลเร็วหรือรู้ว่าต้องเปิดกล่องแล้ว
    // ตอนโชว์แล้วถามทุก 15 วิ พอ เพราะเลขเดินจาก socket อยู่แล้ว
    const light = setInterval(refreshPosition, isWaiting ? 15000 : 5000)
    const heavy = setInterval(refreshRegistered, 60000)

    // กลับมาที่แท็บ = ขอเลขสด ๆ ทันที (ตอนแท็บถูกซ่อน timer ถูกเบรกโดยเบราว์เซอร์)
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshPosition()
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      clearInterval(light)
      clearInterval(heavy)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [ticketId, isWaiting, courseUuid, trainingPlanUuid])

  // popup เปิดอยู่ ห้ามสกรอลล์หน้าเบื้องหลัง (ต้องล็อกทั้ง html และ body เพราะตัวที่สกรอลล์จริงคือ html)
  useEffect(() => {
    if (!isWaiting) return
    const root = document.documentElement
    const prev = {
      root: root.style.overflow,
      body: document.body.style.overflow,
    }
    root.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'
    return () => {
      root.style.overflow = prev.root
      document.body.style.overflow = prev.body
    }
  }, [isWaiting])

  if (!isWaiting) return <></>

  return (
    <StyledQueueOverlay>
      <StyledQueueCard>
        <Typography
          sx={{
            fontSize: { xs: 16, sm: 18 },
            fontWeight: 600,
            textAlign: 'center',
            color: 'text.primary',
          }}
        >
          ระบบได้รับคำขอสมัครอบรมของท่านแล้ว
        </Typography>

        {/* พระเอกของกล่องคือเลขคิว ไม่ใช่ข้อความ */}
        {isMyTurn ? (
          <Column sx={{ alignItems: 'center', gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: { xs: 22, sm: 26 },
                fontWeight: 700,
                color: 'primary.main',
                textAlign: 'center',
              }}
            >
              ถึงลำดับของท่านแล้ว
            </Typography>
            <Typography sx={{ fontSize: 15, color: 'text.secondary' }}>
              ระบบกำลังบันทึกการสมัคร
            </Typography>
          </Column>
        ) : (
          <Column sx={{ alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography
                sx={{
                  fontSize: { xs: 48, sm: 60 },
                  fontWeight: 700,
                  lineHeight: 1,
                  color: 'primary.main',
                }}
              >
                {remaining.toLocaleString()}
              </Typography>
              <Typography
                sx={{ fontSize: 20, fontWeight: 600, color: 'primary.main' }}
              >
                คิว
              </Typography>
            </Box>
          </Column>
        )}

        <Box
          sx={{
            width: '100%',
            // ใหญ่เต็มที่บนจอปกติ แต่ยุบให้พอดีบนจอเตี้ย ไม่ให้การ์ดล้น
            height: 'clamp(132px, 26dvh, 232px)',
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <Lottie
            style={{ height: '100%', width: 'auto' }}
            animationData={lottieLogin}
          />
        </Box>

        <StyledLoadingProgress>
          {Array(20)
            .fill()
            .map((e, index) => (
              <StyledProgressBox key={index}>
                <StyledProgressLoad className="progress" />
              </StyledProgressBox>
            ))}
        </StyledLoadingProgress>

        <Typography
          sx={{
            fontSize: { xs: 15, sm: 16 },
            lineHeight: 1.6,
            textAlign: 'center',
            color: 'text.secondary',
            maxWidth: 520,
          }}
        >
          ท่านออกจากหน้านี้ไปสมัครหลักสูตรอื่นต่อได้
          {/* จอมือถือดันประโยคหลังลงบรรทัดใหม่ ไม่ให้อ่านต่อกันเป็นพืด */}
          <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
            {' '}
            ระบบจะคงลำดับคิวไว้ และแจ้งผลทางอีเมล
          </Box>
        </Typography>

        <Button
          data-testid="btn-queue-course"
          variant="contained"
          size="m"
          endIcon={<ArrowForward />}
          onClick={() => (window.location.href = '/course')}
        >
          ไปหน้าหลักสูตรทั้งหมด
        </Button>
      </StyledQueueCard>
    </StyledQueueOverlay>
  )
}

export default QueueWaiting
